import React, { useEffect } from 'react';
import { Paper, Typography, Box, Button, CircularProgress } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../State/Store';
import { fetchSellerOrders } from '../../../State/seller/sellerOrderSlice';
import { fetchSellerProducts } from '../../../State/seller/sellerProductSlice';
import TransactionTable from '../Payment/Transaction';

export default function Dashboard() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Extract slices from Redux store
  const { sellerOrder, sellerProduct, seller } = useAppSelector((store) => store);

  useEffect(() => {
    const jwt = localStorage.getItem('jwt') || '';
    if (jwt) {
      dispatch(fetchSellerOrders(jwt));
      dispatch(fetchSellerProducts(jwt));
    }
  }, [dispatch]);

  const orders = sellerOrder.orders || [];
  const products = sellerProduct.products || [];
  const profile = seller.profile || seller.profile;

  // 1. Calculate Total Revenue from delivered / non-cancelled orders
  const totalRevenue = orders
    .filter((order: any) => order.orderStatus !== 'CANCELLED')
    .reduce((sum: number, order: any) => {
      // Safely check order total selling price or sum order items
      const orderTotal =
        order.totalSellingPrice ||
        order.orderItems?.reduce(
          (itemSum: number, item: any) =>
            itemSum + (item.product?.sellingPrice || 0) * (item.quantity || 1),
          0
        ) ||
        0;
      return sum + orderTotal;
    }, 0);

  // 2. Calculate Active Products
  const activeProductsCount = products.length;

  // 3. Calculate Total Orders
  const totalOrdersCount = orders.length;

  // 4. Calculate Completed Order Rate / Growth Metric
  const completedOrders = orders.filter((order: any) => order.orderStatus === 'DELIVERED').length;
  const completionRate = totalOrdersCount > 0 ? ((completedOrders / totalOrdersCount) * 100).toFixed(1) : '0';

  const stats = [
    {
      title: 'Total Revenue',
      value: `KES ${totalRevenue.toLocaleString()}`,
      change: 'Lifetime',
      icon: <AttachMoneyIcon className="text-emerald-500" />,
    },
    {
      title: 'Total Orders',
      value: totalOrdersCount.toString(),
      change: `${completedOrders} Delivered`,
      icon: <ShoppingCartIcon className="text-blue-500" />,
    },
    {
      title: 'Active Products',
      value: activeProductsCount.toString(),
      change: 'In Catalog',
      icon: <Inventory2Icon className="text-indigo-500" />,
    },
    {
      title: 'Fulfillment Rate',
      value: `${completionRate}%`,
      change: 'Completed',
      icon: <TrendingUpIcon className="text-teal-500" />,
    },
  ];

  const isLoading = sellerOrder.loading || sellerProduct.loading;

  return (
    <Box className="space-y-8">
      {/* Header Banner */}
      <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-gradient-to-r from-slate-900 to-slate-800 p-6 rounded-2xl text-white shadow-lg">
        <div>
          <Typography variant="h4" className="font-extrabold tracking-tight">
            Welcome Back, {profile?.sellerName || profile?.businessDetails?.businessName || ''}
          </Typography>
          <Typography variant="body2" className="text-slate-300 mt-1">
            Here is what is happening with your store today.
          </Typography>
        </div>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/seller/add-product')}
          className="bg-teal-500 hover:bg-teal-600 text-white font-semibold py-2.5 px-5 rounded-xl shadow-md"
        >
          Add Product
        </Button>
      </Box>

      {/* Metric Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, idx) => (
          <Paper
            key={idx}
            className="p-5 rounded-2xl border border-slate-100 flex items-center justify-between shadow-sm hover:shadow-md transition-shadow"
          >
            <Box className="space-y-1">
              <Typography variant="caption" className="text-slate-500 font-medium">
                {stat.title}
              </Typography>
              <Typography variant="h5" className="font-bold text-slate-800">
                {isLoading ? <CircularProgress size={20} /> : stat.value}
              </Typography>
              <Typography variant="caption" className="text-emerald-600 font-semibold block">
                {stat.change}
              </Typography>
            </Box>
            <Box className="p-3 bg-slate-50 rounded-xl">{stat.icon}</Box>
          </Paper>
        ))}
      </div>

      {/* Recent Transactions Component */}
      <Box>
        <TransactionTable />
      </Box>
    </Box>
  );
}